// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Code generated by Microsoft (R) AutoRest Code Generator.
// Changes may cause incorrect behavior and will be lost if the code is regenerated.

import type { PagedAsyncIterableIterator, PageSettings } from "@azure/core-paging";
import { setContinuationToken } from "../pagingHelper.js";
import type { SharedGalleryImages } from "../operationsInterfaces/index.js";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers.js";
import * as Parameters from "../models/parameters.js";
import type { ComputeManagementClient } from "../computeManagementClient.js";
import type {
  SharedGalleryImage,
  SharedGalleryImagesListNextOptionalParams,
  SharedGalleryImagesListOptionalParams,
  SharedGalleryImagesListResponse,
  SharedGalleryImagesGetOptionalParams,
  SharedGalleryImagesGetResponse,
  SharedGalleryImagesListNextResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Class containing SharedGalleryImages operations. */
export class SharedGalleryImagesImpl implements SharedGalleryImages {
  private readonly client: ComputeManagementClient;

  /**
   * Initialize a new instance of the class SharedGalleryImages class.
   * @param client Reference to the service client
   */
  constructor(client: ComputeManagementClient) {
    this.client = client;
  }

  /**
   * List shared gallery images by subscription id or tenant id.
   * @param location The name of Azure region.
   * @param galleryUniqueName The unique name of the Shared Gallery.
   * @param options The options parameters.
   */
  public list(
    location: string,
    galleryUniqueName: string,
    options?: SharedGalleryImagesListOptionalParams,
  ): PagedAsyncIterableIterator<SharedGalleryImage> {
    const iter = this.listPagingAll(location, galleryUniqueName, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listPagingPage(location, galleryUniqueName, options, settings);
      },
    };
  }

  private async *listPagingPage(
    location: string,
    galleryUniqueName: string,
    options?: SharedGalleryImagesListOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<SharedGalleryImage[]> {
    let result: SharedGalleryImagesListResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._list(location, galleryUniqueName, options);
      const page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listNext(location, galleryUniqueName, continuationToken, options);
      continuationToken = result.nextLink;
      const page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listPagingAll(
    location: string,
    galleryUniqueName: string,
    options?: SharedGalleryImagesListOptionalParams,
  ): AsyncIterableIterator<SharedGalleryImage> {
    for await (const page of this.listPagingPage(location, galleryUniqueName, options)) {
      yield* page;
    }
  }

  /**
   * List shared gallery images by subscription id or tenant id.
   * @param location The name of Azure region.
   * @param galleryUniqueName The unique name of the Shared Gallery.
   * @param options The options parameters.
   */
  private _list(
    location: string,
    galleryUniqueName: string,
    options?: SharedGalleryImagesListOptionalParams,
  ): Promise<SharedGalleryImagesListResponse> {
    return this.client.sendOperationRequest(
      { location, galleryUniqueName, options },
      listOperationSpec,
    );
  }

  /**
   * Get a shared gallery image by subscription id or tenant id.
   * @param location The name of Azure region.
   * @param galleryUniqueName The unique name of the Shared Gallery.
   * @param galleryImageName The name of the Shared Gallery Image Definition from which the Image
   *                         Versions are to be listed.
   * @param options The options parameters.
   */
  get(
    location: string,
    galleryUniqueName: string,
    galleryImageName: string,
    options?: SharedGalleryImagesGetOptionalParams,
  ): Promise<SharedGalleryImagesGetResponse> {
    return this.client.sendOperationRequest(
      { location, galleryUniqueName, galleryImageName, options },
      getOperationSpec,
    );
  }

  /**
   * ListNext
   * @param location The name of Azure region.
   * @param galleryUniqueName The unique name of the Shared Gallery.
   * @param nextLink The nextLink from the previous successful call to the List method.
   * @param options The options parameters.
   */
  private _listNext(
    location: string,
    galleryUniqueName: string,
    nextLink: string,
    options?: SharedGalleryImagesListNextOptionalParams,
  ): Promise<SharedGalleryImagesListNextResponse> {
    return this.client.sendOperationRequest(
      { location, galleryUniqueName, nextLink, options },
      listNextOperationSpec,
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const listOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/providers/Microsoft.Compute/locations/{location}/sharedGalleries/{galleryUniqueName}/images",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.SharedGalleryImageList,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion3, Parameters.sharedTo],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.location,
    Parameters.galleryUniqueName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/providers/Microsoft.Compute/locations/{location}/sharedGalleries/{galleryUniqueName}/images/{galleryImageName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.SharedGalleryImage,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion3],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.location,
    Parameters.galleryImageName,
    Parameters.galleryUniqueName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.SharedGalleryImageList,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  urlParameters: [
    Parameters.$host,
    Parameters.nextLink,
    Parameters.subscriptionId,
    Parameters.location,
    Parameters.galleryUniqueName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
